import { useState, useCallback } from 'react';
import { apiService } from '../services/api';
import { emptyPortfolioKPIs, PortfolioKPIData } from '../contexts/PortfolioKPIContext';
import { GoldPositionFields, goldFieldsFrom } from '../types/position';

/**
 * A position as the screens use it: the leg's user-table fields plus its
 * gold.v_position figures (GoldPositionFields). Gold v2 phase 3b: copies
 * only — no sums, yields, theta sign flips or DTE maths in the browser.
 */
export interface LedgerPosition extends GoldPositionFields {
  id: string;
  ticker: string;
  entryDate: string;
  expiryDate: string;
  strikePrice: number;
  entryPremium: number;
  /** The leg's stock entry (the stock price when this leg opened). */
  entryPrice: number;
  numContracts: number;
  positionType: string;            // 'covered_call' | 'cash_secured_put'
  isWheel?: boolean;
  effectiveCostBasis?: number | null;
  keepSharesPreference?: boolean;
  intent?: 'hold' | 'wheel' | 'release';
  thesisBroken?: boolean;
  status?: string;
  lastAction?: string;
  closePrice?: number | null;
  closeDate?: string | null;
  closePremium?: number | null;
  currentPrice?: number | null;    // glossary stock_price (gold.stock_mark)
  changePercent?: number | null;
  currentPremium?: number | null;  // glossary option_mark
  currentDelta?: number | null;
  daysToExpiration?: number | null; // glossary dte (server, New York)
  impliedVolatility?: number | null;
  stockPriceLastUpdated?: string | null;
  optionPriceLastUpdated?: string | null;
  entryDelta?: number | null;
  entryTheta?: number | null;
  entryContractIv?: number | null;
  disciplineAction?: string | null;
  campaignEntryDate?: string | null;
  campaignId?: string | null;
  legNumber?: number | null;
  legCount?: number | null;
  nextEarningsDate?: string | null;
}

export const toLedgerPosition = (pos: any): LedgerPosition => ({
  ...goldFieldsFrom(pos),
  id: pos.id,
  ticker: pos.ticker,
  entryDate: pos.entry_date,
  expiryDate: pos.expiry_date,
  strikePrice: pos.strike_price,
  entryPremium: pos.entry_premium,
  entryPrice: pos.entry_price,
  numContracts: pos.num_contracts || 1,
  positionType: pos.position_type || 'covered_call',
  isWheel: pos.is_wheel,
  effectiveCostBasis: pos.effective_cost_basis,
  keepSharesPreference: pos.keep_shares_preference,
  intent: pos.intent,
  thesisBroken: pos.thesis_broken,
  status: pos.status,
  lastAction: pos.last_action,
  closePrice: pos.close_price,
  closeDate: pos.close_date,
  closePremium: pos.close_premium,
  currentPrice: pos.current_price,
  changePercent: pos.change_percent,
  currentPremium: pos.current_premium,
  currentDelta: pos.delta,
  daysToExpiration: pos.days_to_expiration,
  impliedVolatility: pos.implied_volatility,
  stockPriceLastUpdated: pos.stock_price_last_updated,
  optionPriceLastUpdated: pos.option_price_last_updated,
  entryDelta: pos.entry_delta,
  entryTheta: pos.entry_theta,
  entryContractIv: pos.entry_contract_iv,
  disciplineAction: pos.discipline_action,
  campaignEntryDate: pos.campaign_entry_date,
  campaignId: pos.campaign_id,
  legNumber: pos.leg_number,
  legCount: pos.leg_count,
  nextEarningsDate: pos.next_earnings_date,
});

export const usePortfolioKPILoader = () => {
  const [isLoading, setIsLoading] = useState(false);

  const loadKPIs = useCallback(async (): Promise<PortfolioKPIData> => {
    setIsLoading(true);
    try {
      if (!localStorage.getItem('token')) return emptyPortfolioKPIs(false);
      // status=active: open legs + legs that ended today, the headers
      // (gold.portfolio_current) and the assigned-shares question, in one call.
      const res = await apiService.getPortfolioPositions('active');
      const rows = res.positions.map(toLedgerPosition);
      const summary = res.summary?.all ?? null;
      return {
        loading: false,
        activePositions: rows.filter((p) => p.isOpen !== false),
        closedTodayPositions: rows.filter((p) => p.isOpen === false),
        summary,
        summaryByLens: res.summary ?? {},
        assignedShares: res.assigned_shares ?? [],
        positionCount: summary?.position_count ?? 0,
        snapshotAsOf: res.snapshot_as_of || undefined,
      };
    } catch (error) {
      console.error('Failed to load portfolio positions:', error);
      return emptyPortfolioKPIs(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { loadKPIs, isLoading };
};
